import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { IconButton, Table, TableBody, TableHead, TablePagination } from "@mui/material";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import {
  HeaderTableCell,
  TableCell,
  TableContainer,
  TableHeaderRow,
  TableRow,
} from "@/components/molecules/Table/styled";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "@/constants/pagination";
import { COMPANY_PERMISSIONS } from "@/constants/modules.permissions";
import useHasNoPermission from "@/hooks/use-check-permissions";
import usePopOver from "@/hooks/use-popover";
import { IServiceHours } from "@/interfaces/service-hours.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";
import { Pagination } from "@/types/page.types";
import { PaginatedResponse } from "@/types/requests.types";

import ServiceHoursPopover from "./ServiceHoursPopover";
import {
  formatTimeDisplay,
  getWeekDayLabel,
} from "../../constants";

type Props = {
  items: IServiceHours[];
  messages: ConfigurationTabMessages;
  data: PaginatedResponse<IServiceHours>;
  pagination: Pagination;
  onRemove: (item: IServiceHours) => void;
  onEdit: (item: IServiceHours) => void;
  onOpenCreate: () => void;
  isEmpty?: boolean;
};

function ServiceHoursTableRow({
  item,
  index,
  messages,
  onRemove,
  onEdit,
}: {
  item: IServiceHours;
  index: number;
  messages: ConfigurationTabMessages;
  onRemove: (item: IServiceHours) => void;
  onEdit: (item: IServiceHours) => void;
}) {
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<IServiceHours>();

  return (
    <TableRow index={index}>
      <TableCell>{getWeekDayLabel(item.weekDay, messages)}</TableCell>
      <TableCell>{formatTimeDisplay(item.startOperationHour)}</TableCell>
      <TableCell>{formatTimeDisplay(item.endOperationHour)}</TableCell>
      <TableCell>{item.description}</TableCell>
      <TableCell>
        <IconButton onClick={(e) => openPopover(e, item)}>
          <MoreHorizIcon />
        </IconButton>
        {selected && (
          <ServiceHoursPopover
            isOpen={isOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            messages={messages}
            selected={selected}
            onRemove={onRemove}
            onEdit={onEdit}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function ServiceHoursTable({
  items,
  messages,
  data,
  pagination,
  onRemove,
  onEdit,
  onOpenCreate,
  isEmpty = false,
}: Props) {
  const hasNoPermissionToEdit = useHasNoPermission(COMPANY_PERMISSIONS.EDIT);

  return (
    <MDBox width="100%">
      <MDBox display="flex" justifyContent="flex-end" mb={1}>
        <MDButton
          variant="gradient"
          color="dark"
          endIcon={<AddCircleOutlineIcon />}
          onClick={onOpenCreate}
          disabled={hasNoPermissionToEdit}
        >
          {isEmpty ? messages.serviceHoursBulkSetupButton : messages.serviceHoursNewButton}
        </MDButton>
      </MDBox>

      <TableContainer>
        <Table stickyHeader aria-label="service-hours-table">
          <TableHead>
            <TableHeaderRow>
              <HeaderTableCell>{messages.serviceHoursWeekDayColumn}</HeaderTableCell>
              <HeaderTableCell>{messages.serviceHoursStartHourColumn}</HeaderTableCell>
              <HeaderTableCell>{messages.serviceHoursEndHourColumn}</HeaderTableCell>
              <HeaderTableCell>{messages.serviceHoursDescriptionColumn}</HeaderTableCell>
              <HeaderTableCell>{messages.serviceHoursOptionsColumn}</HeaderTableCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow index={0}>
                <TableCell colSpan={5} align="center">
                  <MDTypography variant="body2">{messages.serviceHoursEmptyMessage}</MDTypography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item, index) => (
                <ServiceHoursTableRow
                  key={item.id}
                  item={item}
                  index={index}
                  messages={messages}
                  onRemove={onRemove}
                  onEdit={onEdit}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        labelRowsPerPage={messages.rowsPerPage}
        rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={data.totalCount}
        onRowsPerPageChange={(e) => pagination.extend(Number(e.target.value))}
        rowsPerPage={pagination.pageSize}
        page={pagination.pageNumber - 1}
        onPageChange={(_, pageNumber) => pagination.changePage(pageNumber)}
      />
    </MDBox>
  );
}

export default ServiceHoursTable;
